

import React from 'react';
import { cn } from '@/utils/cn';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
// Typography components removed - using standard HTML elements
import { cva, type VariantProps } from 'class-variance-authority';

const contentCardVariants = cva('border-border/70 shadow-[0_1px_3px_rgba(15,23,42,0.08)] transition-shadow duration-200', {
    variants: {
        variant: {
            default: 'border',
            elevated: 'border shadow-[0_12px_32px_rgba(15,23,42,0.12)]',
            interactive: 'border cursor-pointer hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)]',
            danger: 'border-destructive/20 bg-destructive/5',
            warning: 'border-amber-300/60 bg-amber-50/80',
            success: 'border-emerald-200 bg-emerald-50/80'
        },
        size: {
            default: '',
            compact: '',
            large: ''
        }
    },
    defaultVariants: {
        variant: 'default',
        size: 'default'
    }
});

export interface ContentCardProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof contentCardVariants> {
    title?: string;
    description?: string;
    icon?: React.ReactNode;
    actions?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * 標準化されたパターンに基づく一貫したコンテンツカードコンポーネント
 */
export const ContentCard = React.forwardRef<HTMLDivElement, ContentCardProps>(
    ({ className, variant, size, title, description, icon, actions, children, ...props }, ref) => {
        return (
            <Card
                ref={ref}
                className={cn(contentCardVariants({ variant, size }), className)}
                {...props}
            >
                {(title || description || icon || actions) && (
                    <CardHeader className={cn(
                        'pb-4',
                        size === 'compact' && 'p-4 pb-2',
                        size === 'large' && 'p-6 pb-5'
                    )}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                {icon && (
                                    <div className="flex-shrink-0">
                                        {icon}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    {title && (
                                        <CardTitle className="text-base font-semibold">
                                            {title}
                                        </CardTitle>
                                    )}
                                    {description && (
                                        <CardDescription className="mt-1">
                                            {description}
                                        </CardDescription>
                                    )}
                                </div>
                            </div>
                            {actions && (
                                <div className="flex-shrink-0">
                                    {actions}
                                </div>
                            )}
                        </div>
                    </CardHeader>
                )}
                <CardContent className={cn(
                    size === 'compact' && 'p-4 pt-0',
                    size === 'large' && 'p-6 pt-0'
                )}>
                    {children}
                </CardContent>
            </Card>
        );
    }
);

ContentCard.displayName = 'ContentCard';

/**
 * 情報を表示するためのインフォカード
 */
export const InfoCard = React.forwardRef<HTMLDivElement, Omit<ContentCardProps, 'variant'>>(
    (props, ref) => <ContentCard ref={ref} variant="default" {...props} />
);

/**
 * クリック可能なコンテンツ向けインタラクティブカード
 */
export const InteractiveCard = React.forwardRef<HTMLDivElement, Omit<ContentCardProps, 'variant'>>(
    (props, ref) => <ContentCard ref={ref} variant="interactive" {...props} />
);

/**
 * 注意/警告を示す警告カード
 */
export const WarningCard = React.forwardRef<HTMLDivElement, Omit<ContentCardProps, 'variant'>>(
    (props, ref) => <ContentCard ref={ref} variant="warning" {...props} />
);

/**
 * エラー状態を示すエラーカード
 */
export const ErrorCard = React.forwardRef<HTMLDivElement, Omit<ContentCardProps, 'variant'>>(
    (props, ref) => <ContentCard ref={ref} variant="danger" {...props} />
);

/**
 * 成功時のフィードバック用サクセスカード
 */
export const SuccessCard = React.forwardRef<HTMLDivElement, Omit<ContentCardProps, 'variant'>>(
    (props, ref) => <ContentCard ref={ref} variant="success" {...props} />
);

InfoCard.displayName = 'InfoCard';
InteractiveCard.displayName = 'InteractiveCard';
WarningCard.displayName = 'WarningCard';
ErrorCard.displayName = 'ErrorCard';
SuccessCard.displayName = 'SuccessCard';
